<template>
  <Modal @modalClose="onClose">
    <div class="modal-header">
      <h4 v-html="generateModalTitle()" />
      <div class="actions">
        <small class="text-danger mr-3" v-if="isServerError()">
          พบข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองอีกครั้งหรือติดต่อผู้ดูแลระบบ
        </small>
      </div>
    </div>
    <div class="modal-body" v-if="!loadCollectionsState.error">
      <div class="search-container">
        <input
          class="form-control form-control-search"
          :value="filter.q"
          v-if="!isEmpty()"
          :placeholder="getPlaceHolderOnSearch()"
          @keyup.enter="onSearch"/>
      </div>
      <div class="collection-list-container">
        <div class="content" v-if="!loadCollectionsState.isLoading">
          <div
            class="list-item-container no-underline"
            v-for="(collection, collectionIndex) in collections"
            :key="collectionIndex">
            <div class="list-item-image">
              <ImageView
                :source="collection.coverImage" />
            </div>
            <div class="list-item-detail">
              <div class="list-item list-item-primary list-item-title">
                <CollectionInformationModal :collectionItem="collection" />
              </div>
              <div class="list-item list-item-action">
                <div class="action-container">
                  <div
                    v-if="isSavingCollectionIds(collection.id)"
                    class="spinner-border text-secondary"
                    role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <button
                    type="button"
                    class="btn btn-light btn-action"
                    :class="{ active: isSelected(collection.id) }"
                    @click="onSelected(collection.id)"
                    v-if="!isSavingCollectionIds(collection.id)">
                    <i class="far fa-plus-square"
                      v-if="!isSelected(collection.id)"></i>
                    <i class="fas fa-minus-square"
                      v-if="isSelected(collection.id)"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- empty list --->
          <EmptyMessage v-if="isEmpty()">
            <h3>ไม่มี{{ generateCollectionTitle() }}</h3>
          </EmptyMessage>
          <EmptyMessage v-if="isEmptySearch()">
            <h3>ไม่พบ{{ generateCollectionTitle() }}ที่ค้นหา</h3>
            <button
              type="button"
              class="btn btn-light btn-lg mt-4"
              @click="clearSearch">
              ล้างการค้นหา
            </button>
          </EmptyMessage>
        </div>
      </div>
    </div>
    <!-- error from api -->
    <ErrorMessageCollectionModal
      v-if="loadCollectionsState.error"
      :error="loadCollectionsState.error"/>
    <div class="modal-footer">
      <PaginationModal
        :lastPage="lastPage"
        :currentPage.sync="filter.page"
        v-if="isShowPagination()"/>
      <button type="button" @click.stop="onClose" class="btn btn-outline-dark">
        ปิด
      </button>
    </div>
  </Modal>
</template>

<script>
// import Vue from 'vue';
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import Modal from '@/components/Modal.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import PaginationModal from '@/components/PaginationModal.vue';
import ImageView from '@/image/ImageView.vue';

import {
  loadCollections,
  addItemCollection,
  checkExistingItemCollection,
  deleteExistingItemCollection,
} from './api';
import {
  generateModalTitleByCollectionType,
  getCollectionTypeName,
} from './collectionManagement';
import CollectionInformationModal from './CollectionInformationModal.vue';
import ErrorMessageCollectionModal from './ErrorMessageCollectionModal.vue';


export default {
  mixins: [dateFormetter],
  props: {
    itemId: {
      type: String,
    },
    collectionType: {
      type: String,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      filter: {
        type: this.collectionType,
        q: null,
        isDynamic: false,
      },
      collections: [],
      existCollections: [],
      selectedCollectionIds: [],
      saveCollectionTimer: {},
      savingCollectionIds: [],
      saveItem: null,
      saveItemError: null,
      loadingSpinnerTimer: 1000,
      lastPage: null,
      loadCollectionsState: {
        isLoading: true,
        error: null,
      },
      loadExistingCollection: {
        isLoading: true,
        error: null,
      },
    };
  },
  components: {
    Modal,
    ImageView,
    CollectionInformationModal,
    EmptyMessage,
    ErrorMessageCollectionModal,
    PaginationModal,
  },
  created() {
    this.load(this.filter);
    this.checkExistItem(this.itemId);
  },
  watch: {
    filter: {
      handler(newValue) {
        this.load(newValue);
      },
      deep: true,
    },
  },
  methods: {
    async checkExistItem(id) {
      if (id) {
        try {
          const response = await checkExistingItemCollection(id);
          this.existCollections = response.data.data;
          _.forEach(this.existCollections, (value) => {
            if (
              !_.includes(this.selectedCollectionIds, value.collectionDetailId)
            ) {
              this.selectedCollectionIds.push(value.collectionDetailId);
            }
          });
          this.loadExistingCollection.isLoading = false;
        } catch (error) {
          this.loadExistingCollection.error = error.response;
          this.loadExistingCollection.isLoading = false;
        }
      }
    },
    async load(filter) {
      try {
        const response = await loadCollections(filter);
        this.collections = response.data.data;
        this.lastPage = response.data.lastPage;
        this.loadCollectionsState.isLoading = false;
      } catch (error) {
        this.loadCollectionsState.error = error.response;
        this.loadCollectionsState.isLoading = false;
      }
    },
    isSelected(collectionId) {
      return _.find(
        this.selectedCollectionIds,
        (collection) => collection === collectionId,
      );
    },
    async onSelected(collectionId) {
      // TODO :: Call API to submit item into collection
      if (!this.isSelected(collectionId)) {
        this.onSavingCollection(collectionId);
      } else {
        try {
          const deleteItem = _.find(this.existCollections, {
            collectionDetailId: collectionId,
          });
          await deleteExistingItemCollection(collectionId, deleteItem.id);
          this.selectedCollectionIds = _.filter(
            this.selectedCollectionIds,
            (collection) => collection !== collectionId,
          );
        } catch (error) {
          this.saveItemError = error.response.status;
        }
      }
    },
    generateModalTitle() {
      return generateModalTitleByCollectionType(this.collectionType);
    },
    generateCollectionTitle() {
      if (getCollectionTypeName(this.collectionType) === 'เพลย์ลิสต์') {
        return getCollectionTypeName(this.collectionType);
      }
      return `ชุด${getCollectionTypeName(this.collectionType)}`;
    },
    getPlaceHolderOnSearch() {
      const collectionTypeName = getCollectionTypeName(this.collectionType);
      return `ค้นหาด้วยชื่อ${collectionTypeName}`;
    },
    onSearch(event) {
      if (this.filter.page) delete this.filter.page;
      this.filter.q = event.target.value;
    },
    clearSearch() {
      this.filter.q = '';
    },
    isEmpty() {
      return _.isEmpty(this.collections) && this.filter.q === null;
    },
    isEmptySearch() {
      return _.isEmpty(this.collections) && this.filter.q;
    },
    async onSavingCollection(collectionId) {
      this.savingCollectionIds.push(collectionId);
      try {
        const itemResponse = await addItemCollection(collectionId, {
          itemId: this.itemId,
        });
        this.checkExistItem(this.itemId);
        this.saveItem = itemResponse.data;
        this.saveCollectionTimer[collectionId] = setInterval(
          () => {
            this.removeSavingCollectionIds(collectionId);
            clearInterval(this.saveCollectionTimer[collectionId]);
          },
          this.loadingSpinnerTimer,
        );
      } catch (error) {
        this.removeSavingCollectionIds(collectionId);
        this.saveItemError = error.response.status;
      }
    },
    removeSavingCollectionIds(collectionId) {
      this.savingCollectionIds = _.filter(
        this.savingCollectionIds,
        (collectionItem) => collectionItem !== collectionId,
      );
    },
    isSavingCollectionIds(itemId) {
      return _.find(
        this.savingCollectionIds,
        (collectionId) => collectionId === itemId,
      );
    },
    isServerError() {
      return this.saveItemError;
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
@import "./src/assets/scss/themes/helpers.scss";
@import "./src/assets/scss/themes/components/list-manage.scss";

.collection-list-container {
  @extend .scrollbar;
  height: 360px;

  .list-item-container {
    .list-item-image {
      flex: 0 0 140px;
    }

    .btn-action.active {
      background-color: lighten($primary, 75%);
      color: darken($primary, 5%);
    }
  }
}
</style>
